/**
 * Teams: who can work on this app (cloud-api's routes/members.ts, DEPLOYMENT.md
 * §35). Lives in Settings beside the custom domain section and follows its
 * shape: one list, one field, the server deciding every rule.
 *
 * What a collaborator gets is the whole point of the copy here, so it is said
 * once in the panel note and not repeated per row: everything the owner can
 * do, from this dashboard, the CLI, or their own coding agent, except delete
 * the app. Anybody with access may invite and remove, the owner included and
 * excepted (the owner cannot be removed, so their row has no button).
 *
 * An invitation never creates an account. When nobody has signed up with the
 * address yet the invitation still waits, and the section says so instead of
 * implying the person can use it today.
 *
 * Emails are never sent to PostHog: the events carry counts and outcomes only.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { Button } from '../primitives/Button';
import { Field } from '../primitives/Field';
import { Notice } from './Notice';
import { SectionPanel } from './SectionPanel';
import { Skeleton } from './Skeleton';

import {
  getTeam,
  inviteCollaborator,
  removeCollaborator,
  revokeInvitation,
  type InviteResult,
  type TeamInvitation,
  type TeamMember,
} from '../../lib/real-api';
import { captureEvent, groupProject } from '../../lib/analytics/posthog';
import { useFormTracker } from '../../lib/analytics/use-form-tracker';
import { useAccount } from '../../lib/auth/use-app-data';
import { ApiError } from '../../lib/http';
import { useAsync } from '../../lib/hooks/use-async';
import { t } from '../../lib/i18n/t';
import type { Project } from '../../lib/types';

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

interface Props {
  project: Project;
}

function failureMessage(err: unknown): { code: string; message: string } {
  if (err instanceof ApiError) return { code: err.code, message: err.message };
  return { code: 'unknown', message: t('That did not go through. Try again.') };
}

export function MembersSection({ project }: Props) {
  const team = useAsync(() => getTeam(project.id), [project.id]);
  const account = useAccount();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<(InviteResult & { email: string }) | null>(null);

  const form = useFormTracker({
    formId: 'project_member_invite',
    formName: 'Invite a collaborator',
    totalFields: 1,
    projectSlug: project.slug,
  });

  const members = team.data?.members ?? [];
  const invitations = team.data?.invitations ?? [];
  const isOwner = (project.role ?? 'owner') === 'owner';

  useEffect(() => {
    if (team.isLoading) return;
    groupProject(project.slug);
    captureEvent('members_section_viewed', {
      project_slug: project.slug,
      role: project.role ?? 'owner',
      collaborator_count: members.filter((m) => m.role === 'collaborator').length,
      pending_invitation_count: invitations.length,
    });
    // Once per load of the list, not on every keystroke elsewhere on the page.
  }, [team.isLoading, project.slug]);

  async function invite(): Promise<void> {
    const value = email.trim();
    if (value === '') return;
    setBusy(true);
    setError(null);
    setSent(null);
    form.trackSubmit({ email_length: value.length });
    captureEvent('project_member_invite_submitted', {
      project_slug: project.slug,
      role: project.role ?? 'owner',
    });
    try {
      const result = await inviteCollaborator(project.id, value);
      form.trackSubmitSuccess();
      captureEvent('project_member_invite_succeeded', {
        project_slug: project.slug,
        account_exists: result.accountExists,
        email_sent: result.emailSent,
      });
      setSent({ ...result, email: value });
      setEmail('');
      team.refetch();
    } catch (err: unknown) {
      const { code, message } = failureMessage(err);
      form.trackSubmitError(code, message);
      captureEvent('project_member_invite_failed', {
        project_slug: project.slug,
        error_code: code,
      });
      setError(message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(member: TeamMember): Promise<void> {
    const leaving = member.accountId === account.id;
    setBusy(true);
    setError(null);
    captureEvent(leaving ? 'project_member_left' : 'project_member_removed', {
      project_slug: project.slug,
    });
    try {
      await removeCollaborator(project.id, member.accountId);
      if (leaving) {
        navigate('/projects');
        return;
      }
      team.refetch();
    } catch (err: unknown) {
      setError(failureMessage(err).message);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(invitation: TeamInvitation): Promise<void> {
    setBusy(true);
    setError(null);
    captureEvent('project_invitation_revoked', { project_slug: project.slug });
    try {
      await revokeInvitation(project.id, invitation.id);
      team.refetch();
    } catch (err: unknown) {
      setError(failureMessage(err).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <SectionPanel
      title={t('People')}
      note={t(
        'Collaborators can do everything you can on this app, from this dashboard, the svcloud CLI, or their own coding agent. Only the owner can delete it.',
      )}
    >
      <div className="set__control">
        {error ? <Notice tone="attention">{error}</Notice> : null}

        {team.isLoading ? (
          <Skeleton count={2} label={t('Loading the people on this app')} />
        ) : (
          <ul className="set__people">
            {members.map((member) => {
              const you = member.accountId === account.id;
              return (
                <li key={member.accountId} className="set__person">
                  <span className="set__person-who">
                    <strong>{member.displayName ?? member.email}</strong>
                    {member.displayName ? (
                      <span className="set__person-email">{member.email}</span>
                    ) : null}
                  </span>
                  <span className="set__person-role">
                    {member.role === 'owner' ? t('Owner') : t('Collaborator')}
                    {you ? ` ${t('(you)')}` : ''}
                  </span>
                  {member.role === 'collaborator' ? (
                    <Button variant="outline" disabled={busy} onClick={() => remove(member)}>
                      {you ? t('Leave') : t('Remove')}
                    </Button>
                  ) : null}
                </li>
              );
            })}
            {invitations.map((invitation) => (
              <li key={invitation.id} className="set__person">
                <span className="set__person-who">
                  <strong>{invitation.email}</strong>
                </span>
                <span className="set__person-role">{t('Invited')}</span>
                <Button variant="outline" disabled={busy} onClick={() => revoke(invitation)}>
                  {t('Cancel invitation')}
                </Button>
              </li>
            ))}
          </ul>
        )}

        {sent ? (
          <Notice tone="quiet">
            <p>
              {sent.emailSent
                ? t('Invitation emailed to {email}.', { email: sent.email })
                : t('Invitation created for {email}. Send them this link:', { email: sent.email })}
            </p>
            {sent.emailSent ? null : <p className="mono">{sent.inviteUrl}</p>}
            {sent.accountExists ? null : (
              <p>
                {t(
                  '{email} does not have an SV Cloud account yet. The invitation waits until they can sign in with that address.',
                  { email: sent.email },
                )}
              </p>
            )}
          </Notice>
        ) : null}

        {USE_MOCK ? (
          <p className="set__say">{t('Inviting people is not part of this demo.')}</p>
        ) : (
          <div className="set__control">
            <Field
              label={t('Invite by email')}
              type="email"
              value={email}
              placeholder="teammate@example.com"
              onChange={(event) => {
                setEmail(event.target.value);
                form.onFieldChange('email');
              }}
              onFocus={() => form.onFieldFocus('email')}
              onBlur={() => form.onFieldBlur('email')}
              disabled={busy}
            />
            <p className="set__say">
              {t(
                'They accept by signing in to SV Cloud with this email address. Inviting someone does not create an account for them.',
              )}
            </p>
            <div className="set__actions">
              <Button variant="outline" loading={busy} disabled={email.trim() === ''} onClick={invite}>
                {t('Send invitation')}
              </Button>
            </div>
          </div>
        )}

        {isOwner ? null : (
          <p className="set__say">
            {t('You are a collaborator on this app. Its owner is the only one who can delete it.')}
          </p>
        )}
      </div>
    </SectionPanel>
  );
}
